import styles from './skeleton.module.css';

interface SkeletonBaseProps {
  /** Satır sayısı. */
  readonly lines?: number;
  readonly lineHeight?: number;
  /**
   * `people`: avatar + iki satırlık kişi listesi iskeleti (çerçeveli satır).
   * `crowd`: tek kalabalık satırı — 40 px daire + 12/10 px iki çizgi
   * (%58 / %76), çerçevesiz; referans bölüm 04 "YÜKLENİYOR · İSKELET".
   * `entities`: kurum/etkinlik listesi — köşeli 36 px kutu + tek çizgi,
   * çerçevesiz (P7 kullanım detayı "YÜKLENİYOR"; genişlikler `widths`ten).
   * `contacts`: iletişim satırları — 36 px köşeli kutu + iki çubuk (64 px
   * alan adı, değişken genişlikte değer) + sağda 36 px kare; satırlar 1 px
   * çizgiyle ayrılır (Claude Design "İletişim" yükleniyor hâli).
   */
  readonly variant?: 'lines' | 'people' | 'crowd' | 'entities' | 'contacts';
  /**
   * `lines` için satır genişlikleri (CSS değeri). Verilmezse eski kural:
   * tümü %100, son satır %60. Gelecek bloğun şeklini birebir taklit etmek
   * gereken yerde (ana sayfa üst alanı: tam genişlik kapak, 150 px düğme)
   * çağıran açıkça yazar.
   */
  readonly widths?: readonly string[];
}

/** Kendi canlı bölgesi olan iskelet: `role="status"` + etiket ZORUNLU. */
interface LiveSkeletonProps extends SkeletonBaseProps {
  /** Erişilebilirlik etiketi (ör. "İçerik yükleniyor"). */
  readonly label: string;
  readonly decorative?: false;
}

/**
 * Kapsayıcı zaten TEK canlı bölgeyse (`role="status"` çağıranda): rol ve
 * etiket basılmaz, blok `aria-hidden` olur. Aynı cümlenin ekran okuyucuya
 * iskelet parçası kadar tekrar duyurulmaması için (ana sayfa üst alanı: bir
 * bölge, dört parça). `label` TİPTE reddedilir — sessizce atılan etiket
 * okuyana "etiket kullanılıyor" dedirtiyordu (tech-lead D8).
 */
interface DecorativeSkeletonProps extends SkeletonBaseProps {
  readonly decorative: true;
  readonly label?: never;
}

export type SkeletonProps = LiveSkeletonProps | DecorativeSkeletonProps;

/** Gelen içeriğin şeklini taklit eden genişlikler (README 8: spinner yok). */
const PERSON_WIDTHS: readonly (readonly [string, string])[] = [
  ['58%', '80%'],
  ['44%', '66%'],
  ['52%', '72%'],
];

/**
 * Kurum listesi iskeleti — P7 kenar katmanı yükleniyor karesi (`skelRows` ilk üç:
 * `42%` / `30%` / `50%`, satır genişliğine oranla; kutu ve oluk satırın içinde).
 */
const ENTITY_WIDTHS: readonly string[] = ['42%', '30%', '50%'];

/** İletişim satırı değer çubuğu genişlikleri (alan adı çubuğu sabit 64 px). */
const CONTACT_WIDTHS: readonly string[] = ['62%', '48%', '70%', '54%'];

/** Kalabalık satırı iskeleti — referans `width:58%` / `width:76%` (bölüm 04). */
const CROWD_WIDTHS: readonly [string, string] = ['58%', '76%'];

/**
 * Skeleton yükleme durumu — ekran okuyucuya tek etiket, göze liste yapısı.
 * 400 ms'den kısa yüklemelerde hiç gösterilmez (çağıran taraf karar verir).
 */
export function Skeleton(props: SkeletonProps) {
  const { lines = 3, lineHeight = 16, variant = 'lines', widths } = props;
  const live = props.decorative
    ? ({ 'aria-hidden': 'true' } as const)
    : ({ role: 'status', 'aria-busy': 'true', 'aria-label': props.label } as const);
  if (variant === 'crowd') {
    return (
      <div className={styles.crowd} {...live}>
        <span aria-hidden="true" className={styles.crowdAvatar} />
        <span aria-hidden="true" className={styles.crowdLines}>
          <span className={styles.crowdLine} style={{ width: CROWD_WIDTHS[0] }} />
          <span
            className={[styles.crowdLine, styles.crowdLineShort].join(' ')}
            style={{ width: CROWD_WIDTHS[1] }}
          />
        </span>
      </div>
    );
  }
  if (variant === 'contacts') {
    return (
      <div className={styles.contacts} {...live}>
        {Array.from({ length: lines }, (_, index) => (
          <span key={index} aria-hidden="true" className={styles.contactRow}>
            <span className={styles.contactBox} />
            <span className={styles.contactLines}>
              <span className={[styles.contactLine, styles.contactLabelLine].join(' ')} />
              <span
                className={styles.contactLine}
                style={{
                  width: widths?.[index] ?? CONTACT_WIDTHS[index % CONTACT_WIDTHS.length] ?? '60%',
                }}
              />
            </span>
            <span className={styles.contactBox} />
          </span>
        ))}
      </div>
    );
  }
  if (variant === 'entities') {
    return (
      <div className={styles.entities} {...live}>
        {Array.from({ length: lines }, (_, index) => (
          <span key={index} aria-hidden="true" className={styles.entityRow}>
            <span className={styles.entityBox} />
            <span
              className={styles.entityLine}
              style={{
                width: widths?.[index] ?? ENTITY_WIDTHS[index % ENTITY_WIDTHS.length] ?? '60%',
              }}
            />
          </span>
        ))}
      </div>
    );
  }
  if (variant === 'people') {
    return (
      <div className={styles.rows} {...live}>
        {Array.from({ length: lines }, (_, index) => {
          const [first, second] = PERSON_WIDTHS[index % PERSON_WIDTHS.length] ?? ['58%', '80%'];
          return (
            <div key={index} aria-hidden="true" className={styles.row}>
              <span className={styles.rowAvatar} />
              <span className={styles.rowLines}>
                <span className={styles.rowLine} style={{ width: first }} />
                <span
                  className={[styles.rowLine, styles.rowLineShort].join(' ')}
                  style={{ width: second }}
                />
              </span>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className={styles.stack} {...live}>
      {Array.from({ length: lines }, (_, index) => (
        <div
          key={index}
          aria-hidden="true"
          className={styles.block}
          style={{
            height: lineHeight,
            width: widths?.[index] ?? (index === lines - 1 ? '60%' : '100%'),
          }}
        />
      ))}
    </div>
  );
}
